import { BigNumberish, ethers } from "ethers";
import { useCallback, useEffect, useState } from "react";

import { ASSETCHAIN, ASSETCHAIN_TESTNET } from "config/chains";
import { getReferralsSummary } from "lib/apis/referral";
import { BN_ZERO } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { decodeReferralCode } from "sdk/utils/referrals";

import {
  AffiliateTotalStats,
  CodeOwnershipInfo,
  JoinedReferral,
  RebateDistribution,
  ReferralCodeStats,
  ReferralsStats,
  TotalReferralsStats,
  TraderReferralTotalStats,
  TierInfo,
} from "../types";
import { getCodeOwnersData } from "./useUserCodesOnAllChain";

const ACTIVE_REFERRAL_CHAIN_IDS = [ASSETCHAIN, ASSETCHAIN_TESTNET];
const REFERRALS_REQUEST_TIMEOUT_MS = 20_000;

function normalizeReferralCode(code: string) {
  if (code?.startsWith("0x") && code.length === 66) {
    return decodeReferralCode(code as any);
  }
  return code;
}

function toBigInt(value: BigNumberish | string | number | undefined) {
  if (value === undefined || value === null) return 0n;
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return Number.isFinite(value) ? BigInt(Math.trunc(value)) : 0n;
  if (value === "") return 0n;
  try {
    if (typeof value === "string" && value.includes(".")) {
      const [integerPart] = value.split(".");
      return BigInt(integerPart || "0");
    }
    return BigInt(value);
  } catch (_error) {
    return 0n;
  }
}

function normalizeAddress(address?: string | null) {
  if (!address || !ethers.isAddress(address)) return undefined;
  return ethers.getAddress(address);
}

function normalizeAddressArray(addresses?: (string | undefined | null)[]) {
  if (!addresses?.length) return [];
  return addresses.reduce((acc, address) => {
    const normalized = normalizeAddress(address);
    if (normalized) acc.push(normalized);
    return acc;
  }, [] as string[]);
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("referrals request timeout")), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export function useReferralsData(
  account?: string | null,
  preferredChainId?: number | null,
  includeDistributions = true
) {
  const [data, setData] = useState<TotalReferralsStats>();
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const refresh = useCallback(() => {
    setRefreshNonce((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const normalizedAccount = account?.toLowerCase();
    if (!normalizedAccount) {
      setData(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    const chainIds = preferredChainId ? [preferredChainId] : ACTIVE_REFERRAL_CHAIN_IDS;

    async function getChainReferralData(chainId: number): Promise<ReferralsStats | undefined> {
      let res;
      try {
        res = await withTimeout(
          getReferralsSummary(normalizedAccount!, { chainId, includeDistributions }),
          REFERRALS_REQUEST_TIMEOUT_MS
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error fetching referral stats for chain ${chainId}:`, error);
        return undefined;
      }

      const affiliateDistributions: RebateDistribution[] = (res.affiliateDistributions || []).flatMap((d) => {
        const receiver = normalizeAddress(d.receiver);
        if (!receiver) return [];

        return [
          {
            typeId: d.typeId,
            receiver,
            markets: normalizeAddressArray(d.markets),
            tokens: normalizeAddressArray(d.tokens),
            amounts: (d.amounts || []).map((a) => toBigInt(a)),
            amountsInUsd: (d.amountsInUsd || []).map((a) => toBigInt(a)),
            timestamp: Number(d.timestamp),
            transactionHash: d.transactionHash,
            id: d.id,
          },
        ];
      });

      const traderDistributions: RebateDistribution[] = (res.traderDistributions || []).flatMap((d) => {
        const receiver = normalizeAddress(d.receiver);
        if (!receiver) return [];

        return [
          {
            typeId: d.typeId,
            receiver,
            markets: normalizeAddressArray(d.markets),
            tokens: normalizeAddressArray(d.tokens),
            amounts: (d.amounts || []).map((a) => toBigInt(a)),
            amountsInUsd: (d.amountsInUsd || []).map((a) => toBigInt(a)),
            timestamp: Number(d.timestamp),
            transactionHash: d.transactionHash,
            id: d.id,
          },
        ];
      });

      //#region Code Ownership Info
      const referralCodes = res.codes || [];
      const otherChainIds = chainIds.filter((otherChainId) => otherChainId !== chainId);
      const allCodesOwnersOnOtherChains = await Promise.allSettled(
        otherChainIds.map(async (otherChainId) => ({
          chainId: otherChainId,
          data: await getCodeOwnersData(otherChainId, normalizedAccount, referralCodes),
        }))
      ).then((allChainsResult) =>
        allChainsResult
          .filter(
            (
              chainResult
            ): chainResult is PromiseFulfilledResult<{
              chainId: number;
              data: CodeOwnershipInfo[] | undefined;
            }> => chainResult.status === "fulfilled"
          )
          .map((r) => r.value)
      );

      const allOwnersOnOtherChainsMap: {
        [code: string]: {
          [chainId: number]: CodeOwnershipInfo;
        };
      } = {};
      for (const chainCodesOwners of allCodesOwnersOnOtherChains) {
        for (const codeOwnershipInfo of chainCodesOwners.data || EMPTY_ARRAY) {
          if (!allOwnersOnOtherChainsMap[codeOwnershipInfo.code]) {
            allOwnersOnOtherChainsMap[codeOwnershipInfo.code] = {};
          }

          allOwnersOnOtherChainsMap[codeOwnershipInfo.code][chainCodesOwners.chainId] = codeOwnershipInfo;
        }
      }
      //#endregion

      const affiliateReferralCodesStats: ReferralCodeStats[] = (res.affiliateReferralCodesStats || []).map(
        (e): ReferralCodeStats => ({
          referralCode: normalizeReferralCode(e.referralCode),
          trades: e.trades,
          tradedReferralsCount: e.tradedReferralsCount,
          registeredReferralsCount: e.registeredReferralsCount,
          allOwnersOnOtherChains: allOwnersOnOtherChainsMap[e.referralCode],
          volume: toBigInt(e.volume),
          totalRebateUsd: toBigInt(e.totalRebateUsd),
          affiliateRebateUsd: toBigInt(e.affiliateRebateUsd),
          discountUsd: toBigInt(e.discountUsd),
          v1Data: {
            volume: toBigInt(e.v1Data.volume),
            totalRebateUsd: toBigInt(e.v1Data.totalRebateUsd),
            discountUsd: toBigInt(e.v1Data.discountUsd),
            affiliateRebateUsd: toBigInt(e.v1Data.affiliateRebateUsd),
          },
          v2Data: {
            volume: toBigInt(e.v2Data.volume),
            totalRebateUsd: toBigInt(e.v2Data.totalRebateUsd),
            discountUsd: toBigInt(e.v2Data.discountUsd),
            affiliateRebateUsd: toBigInt(e.v2Data.affiliateRebateUsd),
          },
        })
      );

      const joinedReferrals: JoinedReferral[] = (res.joinedReferrals || []).flatMap((joined) => {
        const joinedAddress = normalizeAddress(joined.address);
        if (!joinedAddress) return [];

        return [
          {
            address: joinedAddress,
            referralCode: normalizeReferralCode(joined.referralCode),
            referrerAddress: normalizeAddress(joined.referrerAddress) ?? undefined,
            joinedAt: joined.joinedAt ? Math.floor(new Date(joined.joinedAt).getTime() / 1000) : 0,
            tradeVolume: toBigInt(joined.tradeVolume),
            totalFee: toBigInt(joined.totalFee),
            totalRewards: toBigInt(joined.totalRewards),
          },
        ];
      });

      const affiliateTotalStats: AffiliateTotalStats = {
        trades: res.affiliateTotalStats?.trades ?? 0,
        tradedReferralsCount: res.affiliateTotalStats?.tradedReferralsCount ?? 0,
        registeredReferralsCount: res.affiliateTotalStats?.registeredReferralsCount ?? 0,
        volume: toBigInt(res.affiliateTotalStats?.volume),
        totalRebateUsd: toBigInt(res.affiliateTotalStats?.totalRebateUsd),
        affiliateRebateUsd: toBigInt(res.affiliateTotalStats?.affiliateRebateUsd),
        discountUsd: toBigInt(res.affiliateTotalStats?.discountUsd),
        v1Data: {
          volume: toBigInt(res.affiliateTotalStats?.v1Data?.volume),
          totalRebateUsd: toBigInt(res.affiliateTotalStats?.v1Data?.totalRebateUsd),
          affiliateRebateUsd: toBigInt(res.affiliateTotalStats?.v1Data?.affiliateRebateUsd),
          discountUsd: toBigInt(res.affiliateTotalStats?.v1Data?.discountUsd),
        },
        v2Data: {
          volume: toBigInt(res.affiliateTotalStats?.v2Data?.volume),
          totalRebateUsd: toBigInt(res.affiliateTotalStats?.v2Data?.totalRebateUsd),
          affiliateRebateUsd: toBigInt(res.affiliateTotalStats?.v2Data?.affiliateRebateUsd),
          discountUsd: toBigInt(res.affiliateTotalStats?.v2Data?.discountUsd),
        },
      };

      const traderReferralTotalStats: TraderReferralTotalStats = {
        volume: toBigInt(res.traderReferralTotalStats?.volume),
        discountUsd: toBigInt(res.traderReferralTotalStats?.discountUsd),
        v1Data: {
          volume: toBigInt(res.traderReferralTotalStats?.v1Data?.volume),
          discountUsd: toBigInt(res.traderReferralTotalStats?.v1Data?.discountUsd),
        },
        v2Data: {
          volume: toBigInt(res.traderReferralTotalStats?.v2Data?.volume),
          discountUsd: toBigInt(res.traderReferralTotalStats?.v2Data?.discountUsd),
        },
      };

      const affiliateTierInfo: TierInfo = {
        tierId: Number(res.affiliateTierInfo?.tierId ?? 0),
        discountShare: toBigInt(res.affiliateTierInfo?.discountShare),
        id: normalizedAccount!,
      };

      return {
        chainId,
        affiliateDistributions,
        traderDistributions,
        affiliateReferralCodesStats,
        joinedReferrals,
        affiliateTotalStats,
        traderReferralTotalStats,
        codes: (res.codes || []).map((code) => normalizeReferralCode(code)),
        affiliateTierInfo,
      };
    }

    async function loadData() {
      try {
        const allChainsResult = await Promise.allSettled(
          chainIds.map(async (chainId) => ({
            chainId,
            stats: await getChainReferralData(chainId),
          }))
        );

        if (cancelled) return;

        const statsByChain = allChainsResult
          .filter(
            (
              chainResult
            ): chainResult is PromiseFulfilledResult<{
              chainId: number;
              stats: ReferralsStats | undefined;
            }> => chainResult.status === "fulfilled"
          )
          .reduce(
            (acc, cv) => {
              if (cv.value.stats) {
                acc[cv.value.chainId] = cv.value.stats;
              }
              return acc;
            },
            {} as { [chainId: number]: ReferralsStats }
          );

        const totalStats = Object.values(statsByChain).reduce(
          (acc, chainStats) => {
            acc.registeredReferralsCount += chainStats.affiliateTotalStats.registeredReferralsCount || 0;
            acc.affiliateVolume += chainStats.affiliateTotalStats.volume ?? 0n;
            acc.affiliateRebateUsd += chainStats.affiliateTotalStats.affiliateRebateUsd ?? 0n;
            acc.discountUsd += chainStats.affiliateTotalStats.discountUsd ?? 0n;
            acc.traderVolume += chainStats.traderReferralTotalStats.volume ?? 0n;
            return acc;
          },
          {
            registeredReferralsCount: 0,
            affiliateVolume: BN_ZERO,
            affiliateRebateUsd: BN_ZERO,
            discountUsd: BN_ZERO,
            traderVolume: BN_ZERO,
          }
        );

        setData({
          total: totalStats,
          chains: statsByChain,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load referrals data", error);
        if (!cancelled) {
          setData({
            total: {
              registeredReferralsCount: 0,
              affiliateVolume: BN_ZERO,
              affiliateRebateUsd: BN_ZERO,
              discountUsd: BN_ZERO,
              traderVolume: BN_ZERO,
            },
            chains: {},
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [account, preferredChainId, refreshNonce, includeDistributions]);

  return { data, loading, refresh };
}
